





















































































































































import { deepCopy } from '@/jbi-shared/util/deep-copy.util';
import {
  AddressCountry,
  AddressFilterOption,
  CurrencyOptions
} from '@/store/modules/admin/types/group-level-attribute.types';
import {
  CoursePackageComponent,
  UpdateCoursePackageStatePayload
} from '@/store/modules/jtms/course-package/course-package.types';
import { RootState } from '@/store/store';
import { ApiState } from '@/store/types/general.types';
import { Vue, Component, Prop, Watch, Emit } from 'vue-property-decorator';
import { Action, Getter, State } from 'vuex-class';
import { ToastProgrammatic as Toast } from 'buefy';
import {
  CourseModuleComponentItem,
  CourseModuleDropdownSelection,
  IndexBooleanPayload
} from '@/store/modules/jtms/course-module/course-module.types';
import { CourseModuleStatus } from '@/jbi-shared/types/jtms.types';
import { generateTempId } from '@/utils/jtms-utils';

@Component({})
export default class CoursePackageComponentItem extends Vue {
  @Prop({ default: false }) isSettings!: boolean;
  @Prop(Object) coursePackage!: CoursePackageComponent;
  @Prop(Number) index!: number;

  amountErrorMsg: string = '';
  currencyErrorMsg: string = '';
  courseModuleErrorMsg: string = '';

  selectedCurrency: string = this.coursePackage.currency ?? '';
  inputAmount: string =
    this.coursePackage.amount !== undefined &&
    this.coursePackage.amount !== null
      ? this.coursePackage.amount.toFixed(2).toString() // Append value with .00
      : '0.00';
  selectedCourseModuleComponent: CourseModuleDropdownSelection[] = [];

  isActive: boolean = false;
  isInitializing: boolean = true;

  // Cloned array to prevent mutations to state
  clonedCountries: AddressCountry[] = [];
  clonedCurrencies: CurrencyOptions[] = [];

  @Action('admin/getCountries')
  getAllCountries!: (addressFilter?: AddressFilterOption) => void;

  @State(({ admin }: RootState) => admin.apiState.getCountries)
  getAllCountriesApiState!: ApiState;

  @State(({ admin }: RootState) => admin.countries)
  allCountries!: AddressCountry[];

  @State(({ admin }: RootState) => admin.currencies)
  allCurrencies!: CurrencyOptions[];

  @State(
    ({ courseModule }: RootState) =>
      courseModule.courseModulesTabState.activeCourseModules
  )
  activeCourseModules!: CourseModuleComponentItem[];

  @State((state: RootState) => state.course.courseCreationState.isFreeCourse)
  isFreeCourse!: boolean;

  @Getter('courseModule/activeCourseModulesForDropdown')
  readonly activeCourseModulesList!: CourseModuleDropdownSelection[];

  @Action('coursePackage/updateCoursePackage')
  updateCoursePackage!: (payload: UpdateCoursePackageStatePayload) => void;

  @Action('coursePackage/updateCoursePackageFieldError')
  updateCoursePackageFieldError!: (payload: IndexBooleanPayload) => void;

  @State(
    ({ coursePackage }: RootState) =>
      coursePackage.coursePackageTabState.activeCoursePackages
  )
  activeCoursePackages!: CoursePackageComponent[];

  @State(({ course }: RootState) => course.courseCreationState.hasModules)
  hasModules!: boolean;

  assignExistingCourseModules(): CourseModuleDropdownSelection[] {
    if (!this.coursePackage.courseModules) {
      return [];
    }

    const map: Map<number, CourseModuleComponentItem> = new Map(
      this.activeCourseModules.map((cm) => [cm.id!, cm])
    );

    return this.coursePackage.courseModules.map((cm) => {
      const matching = map.get(cm.id!);
      return {
        id: cm.id ? cm.id : generateTempId(),
        label: matching!.label!
      };
    });
  }

  mounted() {
    if (!this.allCountries) {
      this.getAllCountries();
    }

    if (this.allCurrencies) {
      this.clonedCurrencies = this.allCurrencies;
    }

    if (
      this.hasModules &&
      this.coursePackage.courseModules &&
      this.coursePackage.courseModules.length > 0
    ) {
      this.selectedCourseModuleComponent = this.assignExistingCourseModules();
    }

    this.$nextTick(() => {
      this.isInitializing = false;
    });
  }

  get currencyList(): CurrencyOptions[] {
    if (this.clonedCurrencies.length > 0) {
      return this.clonedCurrencies.filter((country) => {
        return country.currency
          .toLowerCase()
          .includes((this.selectedCurrency as string).toLowerCase());
      });
    }

    return [];
  }

  get shouldDisabledDropdown() {
    return this.coursePackage.shouldDisabledCourseModuleDropdown;
  }

  fieldErrorMessages(): string[] {
    return [
      this.courseModuleErrorMsg,
      this.currencyErrorMsg,
      this.amountErrorMsg
    ];
  }

  updateErrorState() {
    const hasFieldError = this.fieldErrorMessages().join('').length > 0;
    this.setFieldErrorForCurrentComponent(hasFieldError);
  }

  setFieldErrorForCurrentComponent(value: boolean) {
    this.updateCoursePackageFieldError({ index: this.index, data: value });
  }

  toggleIsActive() {
    this.isActive = !this.isActive;
  }

  /**
   * This method is triggered when user selects an item from the b-autocomplete dropdown.
   * Although there is v-model binding to the `selectedCurrency` variable, b-autocomplete
   * emits selected items as separate event via `@selected`, so a separate method is required
   * to handle the update.
   */
  assignSelectedCurrency(selectedCountry: AddressCountry | null): void {
    this.selectedCurrency = selectedCountry ? selectedCountry.currency : '';
  }

  /**
   * Check for currency name when user types manually.
   * Assign corrected value (string case) if input matched
   */
  verifyCurrencyInput(): void {
    if (
      this.isSettings &&
      (!this.clonedCurrencies || this.clonedCurrencies.length === 0)
    ) {
      return;
    }

    this.currencyErrorMsg = '';

    // Validate against empty/null values
    if (!this.selectedCurrency && !this.isFreeCourse) {
      this.currencyErrorMsg = `Currency field is required.`;
      return;
    }

    const targetCountry:
      | CurrencyOptions
      | undefined = this.clonedCurrencies.find(
      (country) =>
        country.currency.toLowerCase() ===
        (this.selectedCurrency as string).toLowerCase()
    );

    if (!targetCountry) {
      this.currencyErrorMsg = 'Invalid currency.';
      return;
    }

    if (targetCountry) {
      this.selectedCurrency = targetCountry.currency;
    }

    this.currencyErrorMsg = '';
    return;
  }

  verifyAmount(input?: string): void {
    input = input || (this.inputAmount as string);

    this.amountErrorMsg = '';

    // Required field validation
    if ((!input || input === '0' || input === '0.00') && !this.isFreeCourse) {
      this.amountErrorMsg = `Amount field is required.`;
      return;
    }

    if (input) {
      // Remove any non-numeric characters except for a decimal point
      const sanitizedInput = input.replace(/[^\d.]/g, '');

      // Check if the input has a valid currency format (X.XX)
      const currencyRegex = /^\d*\.?\d{0,2}$/;
      if (!currencyRegex.test(sanitizedInput)) {
        this.amountErrorMsg =
          'Please enter a valid currency amount, e.g. 10.00';
        return;
      }

      // Ensure value provided is a non-negative number
      const numericValue = parseFloat(sanitizedInput);
      if (isNaN(numericValue)) {
        this.amountErrorMsg = 'Please enter a valid number';
      } else if (numericValue < 0) {
        this.amountErrorMsg = 'Amount cannot be negative';
      }

      this.inputAmount = sanitizedInput;
    }
  }

  formatAmount(): void {
    if (this.inputAmount) {
      // Parse the current value to a number
      const numericValue = parseFloat(this.inputAmount as string);

      // If it's a valid number, format it to 2 decimal places
      if (!isNaN(numericValue)) {
        // Format to 2 decimal places
        this.inputAmount = numericValue.toFixed(2);
        return;
      } else {
        // If not a valid number, reset to empty or default value
        this.inputAmount = '';
        return;
      }
    }

    this.inputAmount = '0.00';
  }

  // Only allow numeral inputs
  checkDigit = (event: KeyboardEvent) => {
    if (event.key.length === 1 && isNaN(Number(event.key))) {
      event.preventDefault();
    }
  };

  /**
   * Checks if a given course module object is currently in the selection.
   * This is used to set the checked state of each individual checkbox.
   */
  isSelected(courseModule: CourseModuleDropdownSelection): boolean {
    return this.selectedCourseModuleComponent.some(
      (selected) => selected.id === courseModule.id
    );
  }

  /**
   * Handles the click event from a checkbox.
   * @param courseModule The course module object associated with the checkbox.
   * @param shouldBeSelected The new boolean state from the checkbox's @input event.
   */
  togglePrerequisite(
    courseModule: CourseModuleDropdownSelection,
    shouldBeSelected: boolean
  ): void {
    // Create a new array from the local variable
    let newValue = [...this.selectedCourseModuleComponent];

    if (shouldBeSelected) {
      // Add the course module if it's not already there.
      if (!this.isSelected(courseModule)) {
        newValue.push(courseModule);
      }
    } else {
      // Remove the course module.
      newValue = newValue.filter((selected) => selected.id !== courseModule.id);
    }

    this.selectedCourseModuleComponent = newValue;
  }

  @Watch('getAllCountriesApiState', { deep: true })
  validateCountryCallback(state: ApiState): void {
    if (state.success) {
      this.clonedCountries = deepCopy(this.allCountries);
    }

    if (state.error) {
      Toast.open('Error fetching countries and states.');
    }
  }

  @Watch('selectedCourseModuleComponent', { deep: true })
  onCourseModuleSelectionChange(
    selectedCourseModuleComponent: CourseModuleDropdownSelection[]
  ) {
    if (this.isInitializing) {
      return; // Do not emit during initialization
    }

    this.updateCoursePackage({
      index: this.index,
      data: {
        courseModules: selectedCourseModuleComponent.map((cm) => {
          return {
            id: cm.id,
            label: cm.label,
            status: CourseModuleStatus.ACTIVE
          };
        })
      }
    });
    this.upsertCoursePackage();

    if (this.hasModules && this.selectedCourseModuleComponent.length > 0) {
      this.courseModuleErrorMsg = '';
    } else {
      // this.setFieldErrorForCurrentComponent(true);
      this.courseModuleErrorMsg = 'Course Modules field cannot be empty';
    }
  }

  @Watch('selectedCurrency')
  onCurrencyChange(currency: string) {
    this.updateCoursePackage({
      index: this.index,
      data: { currency }
    });
    this.upsertCoursePackage();

    if (this.selectedCurrency || this.isFreeCourse) {
      this.currencyErrorMsg = '';
    } else {
      this.setFieldErrorForCurrentComponent(true);
      this.currencyErrorMsg = 'Currency field cannot be empty';
    }
  }

  @Watch('inputAmount')
  onAmountChange(amount: number) {
    this.updateCoursePackage({
      index: this.index,
      data: { amount: Number(amount) }
    });
    this.upsertCoursePackage();

    if (this.inputAmount || this.isFreeCourse) {
      this.amountErrorMsg = '';
    } else {
      this.setFieldErrorForCurrentComponent(true);
      this.amountErrorMsg = 'Amount field cannot be empty';
    }
  }

  @Watch('selectedCourseModuleComponent', { deep: true })
  @Watch('selectedCurrency')
  @Watch('inputAmount')
  checkFieldError(): void {
    this.updateErrorState();
  }

  @Watch('isFreeCourse')
  onFreeCourseToggled() {
    this.verifyAmount();
    this.verifyCurrencyInput();
  }

  @Emit('delete-package')
  removeCoursePackage() {
    return this.index;
  }

  @Emit('upsert-package')
  upsertCoursePackage() {
    return this.index;
  }
}
